import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { getPortByBrand } from "@tvg/utils/generalUtils";
import { isSameDay, isAfter, parseISO } from "date-fns";
import { breakpoints, useMediaQuery } from "@tvg/design-system";
import {
  Header,
  Icon,
  LinkCard,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Modal,
  Button,
  Mtp
} from "@tvg/design-system/web";
import { getWagerProfile } from "@urp/store-selectors";
import { RaceStatusEnum } from "@tvg/ts-types/Race";
import { TabEligibleRacesEnum } from "../../types";
import MTPStatusQuery from "../../graphql/queries/MTPStatusUpdate";
import {
  POLL_INTERVAL,
  POLL_INTERVAL_SUBSCRIPTION,
  QUERY_VARIABLES
} from "./constants";
import {
  IconContainer,
  HeaderContainer,
  ButtonSection,
  Section,
  CloseIcon,
  CardContainer,
  HeaderModalContainer
} from "./styled-components";
import { tabListHeader } from "./tabsListContent";

export interface Race {
  trackName: string;
  raceNumber: number;
  url: string;
  mtp: number;
  postTime: string;
  status: RaceStatusEnum;
}

export interface Blok {
  _uid: string;
  title: string;
  modalTitle: string;
  buttonDescription: string;
  races: Race[];
}

export interface Props {
  content: {
    bloks: Blok[];
  };
}

const EligibleRaces = ({ content }: Props) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const wagerProfile = useSelector(getWagerProfile);
  const [contentData, setContent] = useState({
    ...content.bloks[0],
    comingRaces: [] as Race[],
    futureRaces: [] as Race[]
  });
  const [openModal, setOpenModal] = useState(false);
  const shouldRunSubscription = useSelector((store) =>
    get(store, "capi.featureToggles.mtpUpdateSubscriptionMblEnabled", false)
  );
  const [selectedTab, setSelectedTab] = useState(
    TabEligibleRacesEnum.COMING_RACES
  );

  // query variables
  const trackNames = contentData?.races.map(
    (race: { trackName: string }) => race.trackName
  );
  const filterBy = {
    trackName: trackNames
  };
  const variables = {
    ...QUERY_VARIABLES,
    filterBy,
    wagerProfile: wagerProfile || getPortByBrand()
  };

  const { data } = useQuery(MTPStatusQuery, {
    pollInterval: shouldRunSubscription
      ? POLL_INTERVAL_SUBSCRIPTION
      : POLL_INTERVAL,
    fetchPolicy: "cache-and-network",
    ssr: false,
    variables
  });

  useEffect(() => {
    if (data) {
      getMtpStatus();
    }
  }, [data]);

  const getRacesByDate = (races: Race[]) => {
    const today = new Date();
    const comingRaces: Race[] = [];
    const futureRaces: Race[] = [];

    races.forEach((race: Race) => {
      if (!race.postTime) return;
      const raceDate = parseISO(race.postTime);
      if (isSameDay(raceDate, today)) {
        comingRaces.push(race);
      } else if (isAfter(raceDate, today)) {
        futureRaces.push(race);
      }
    });

    return { comingRaces, futureRaces };
  };

  const getMtpStatus = () => {
    const races = contentData.races.map((race: Race) => {
      const item = data?.mtpRaces.find(
        (mtpRace: { trackName: string; number: number }) =>
          mtpRace.trackName === race.trackName &&
          mtpRace.number === race.raceNumber
      );
      return {
        ...race,
        mtp: item?.mtp,
        postTime: item?.postTime,
        status: item?.status?.code
      };
    });

    const { comingRaces, futureRaces } = getRacesByDate(races);

    setContent({ ...contentData, comingRaces, futureRaces });
  };

  const renderList = (items: Race[]) =>
    items.map((item, i) => (
      <LinkCard
        key={`${item.trackName} R${item.raceNumber}`}
        title={`${item.trackName} R${item.raceNumber}`}
        endContent={
          <CardContainer>
            <Mtp
              mtp={item.mtp}
              status={item.status}
              postTime={item.postTime}
              qaLabel="confirm-bet-modal-mtp"
              size="xs"
            />
            <IconContainer>
              <Icon
                name="chevronRight"
                size="s"
                lineColor="--fd-colors-component-button-tertiary-content-base"
              />
            </IconContainer>
          </CardContainer>
        }
        onPress={() => navigate(item.url)}
        qaLabel={`${i}-LinkCard`}
        isLinkItem
      />
    ));

  const renderTabs = () => (
    <TabList qaLabel="tabList" isStretched>
      {Object.entries(tabListHeader).map(([value, title]) => (
        <Tab key={title} value={value} title={title} noBorder />
      ))}
    </TabList>
  );

  const renderViewButton = () => (
    <ButtonSection>
      <Button
        qaLabel="welcome-action-button"
        variant="tertiary"
        onClick={() => setOpenModal(true)}
        borderRadius={4}
      >
        {contentData.buttonDescription}
      </Button>
    </ButtonSection>
  );

  const renderTabsContent = (isExtendedList?: boolean) => (
    <Tabs
      selectedValue={selectedTab}
      onChange={(val) => {
        setSelectedTab(val as TabEligibleRacesEnum);
      }}
      qaLabel="tab"
      isFullHeight
    >
      {renderTabs()}
      <TabPanel value={TabEligibleRacesEnum.COMING_RACES} isFullHeight>
        <>
          {renderList(
            !isExtendedList && contentData.comingRaces.length > 5
              ? contentData.comingRaces.slice(0, 5)
              : contentData.comingRaces
          )}
          {contentData.comingRaces.length >= 5 &&
            !isExtendedList &&
            renderViewButton()}
        </>
      </TabPanel>
      <TabPanel value={TabEligibleRacesEnum.FUTURE_RACES} isFullHeight>
        <>
          {renderList(
            !isExtendedList && contentData.futureRaces.length > 5
              ? contentData.futureRaces.slice(0, 5)
              : contentData.futureRaces
          )}
          {contentData.futureRaces.length > 5 &&
            !isExtendedList &&
            renderViewButton()}
        </>
      </TabPanel>
    </Tabs>
  );

  const onClose = () => {
    setOpenModal(false);
  };

  return (
    <Section isMobile={isMobile}>
      <HeaderContainer isMobile={isMobile}>
        <Header tag="h3" color="--fd-colors-content-strong">
          {contentData.title}
        </Header>
      </HeaderContainer>
      {renderTabsContent()}
      <Modal
        isOpen={openModal}
        onClose={onClose}
        customHeader={
          <>
            <HeaderModalContainer>
              <Header tag="h3" color="--fd-colors-content-strong">
                {contentData.modalTitle}
              </Header>
            </HeaderModalContainer>
            <CloseIcon onClick={onClose}>
              <Icon
                name="close"
                size="m"
                lineColor="--fd-colors-content-subtle"
              />
            </CloseIcon>
          </>
        }
        onAfterClose={onClose}
        padding={0}
        maxWidth="414px"
      >
        {renderTabsContent(true)};
      </Modal>
    </Section>
  );
};

export default EligibleRaces;
